import type { IConfiguration, IConfig } from '@microsoft/applicationinsights-core-js';
import type {
  ApplicationInsights,
  IEventTelemetry,
  IExceptionTelemetry,
  IPageViewTelemetry,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { v4 } from 'uuid';

import { useWebView } from 'shared/src/composables/useWebView.composable';
import AuthorizationModuleFacade from 'shared/src/modules/authModule/store/authorizationModule.facade';

let appInsightsInstance: ApplicationInsights | undefined = undefined;

export type AppInsightsConfig = IConfiguration & IConfig & {
  instrumentationKey?: string;
  viewContext?: ViewContext;
}

export async function initAppInsight(config: AppInsightsConfig|null): Promise<ApplicationInsights | undefined> {
  if (config == null) {
    appInsightsInstance = undefined;

    return;
  }
  if (config.instrumentationKey == null) {
    return;
  }
  const { ApplicationInsights, DistributedTracingModes } = await import('@microsoft/applicationinsights-web');

  const instance = new ApplicationInsights({
    config: {
      instrumentationKey: config.instrumentationKey,
      enableAutoRouteTracking: false,
      enableCorsCorrelation: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      ...config,
    },
  });
  instance.loadAppInsights();
  instance.setAuthenticatedUserContext(AuthorizationModuleFacade.UserMid || '');

  instance.addTelemetryInitializer(item => telemetryInitializer(item, config));
  appInsightsInstance = instance;

  return instance;
}

function getAppInsightInstance(): ApplicationInsights | undefined {
  return appInsightsInstance;
}

const telemetryInitializer: (item: ITelemetryItem, config: AppInsightsConfig) => boolean | void = (
  envelope: ITelemetryItem,
  config: AppInsightsConfig
) => {

  if (envelope?.baseData?.target?.indexOf('sockjs-node') > -1) {
    return false;
  }

  const regex = new RegExp('eUSMobile(?:/([^s]*))?(?=s|$)');
  const matches = regex.exec(window.navigator.userAgent);

  if (matches && matches.length > 1) {
    const telemetryItem = envelope?.data;
    if (telemetryItem !== undefined) {
      telemetryItem['MobileAppVersion'] = matches[1];
    }
  }

  if (envelope) {
    if (!envelope.tags) {
      envelope.tags = [];
    }

    if (!envelope.tags['ai.cloud.role']) {
      const { isWebView } = useWebView();
      envelope.tags!['ai.cloud.role'] = isWebView.value
        ? 'Mof.eUrzad.FrontOffice.Vue.WebView'
        : 'Mof.eUrzad.FrontOffice.Vue';
    }

    if (!envelope.tags['ai.user.id']) {
      envelope.tags!['ai.user.id'] = AuthorizationModuleFacade.UserMid || '';
    }

    const baseData = envelope?.baseData;

    if (baseData && !!config.viewContext) {
      baseData.properties = baseData.properties || {};
      baseData.properties['viewContext'] = config.viewContext;
    }
  }
};


export const trackPageView = (event: IPageViewTelemetry): void => {
  const appInsights = getAppInsightInstance();
  if (appInsights === undefined) {
    return;
  }
  // Generating new ID when user changes page
  const ctx = appInsights.getTraceCtx();
  if (ctx) {
    ctx.setTraceId(v4().replace(/-/g, ''));
  }

  appInsights.trackPageView(event);
  appInsights.flush();
};

export enum IS_ORGANIZATION {
  TRUE = 'True',
  FALSE = 'False'
}

export enum ViewContext {
  KO = 'KO',
  KOF = 'KOF',
  UDK_KO = 'UDK.KO',
  UDK_KOF = 'UDK.KOF',
  WT = 'WT',
  HOST = 'HOST',
  ERROR_HANDLER = 'ERROR_HANDLER',
}

export const sendCustomEvent = (event: IEventTelemetry, isOrganization: null | IS_ORGANIZATION = IS_ORGANIZATION.FALSE): void => {
  // Sending string to match BE format
  const appInsights = getAppInsightInstance();
  if (appInsights === undefined) {
    return;
  }
  appInsights.trackEvent(event, isOrganization == null ? undefined : { isOrganization });
  appInsights.flush();
};

export const sendExceptionEvent = (event: IExceptionTelemetry): void => {
  const appInsights = getAppInsightInstance();
  if (appInsights === undefined) {
    return;
  }
  appInsights.trackException(event);
  appInsights.flush();
};
